import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Modal } from "@/Common/Components/Modals/Modal";
import { FormTemplate } from "@/Common/Models/Forms/FormTemplate";
import { QuestionnaireAnswer } from "@/Common/Models/Questionnaires/QuestionnaireAnswer";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { TemplateFormRenderer } from "./Renderer/TemplateFormRenderer";
import { QuestionnaireAction } from "./QuestionnaireAction";
import { QuestionnaireFormComponent } from "./QuestionnaireFormComponent";

interface IQuestionnaireFormPopupProps {
    questionnaireInstanceId: string;

    onClose: () => void;
    onSubmitted: (result: boolean) => void;
}

class QuestionnaireFormPopupState {
    public formTemplate: FormTemplate;
    public patientName: string;
    public answers: QuestionnaireAnswer[];
}

class QuestionnaireFormPopup extends BaseReactComponent<IQuestionnaireFormPopupProps, QuestionnaireFormPopupState> {
    private componentId: string = "questionnaire-form-popup";
    private action: QuestionnaireAction;

    public componentDidMount(): void {
        this.action = new QuestionnaireAction(errors => this.handleError(errors));

        this.action.loadFormTemplate(this.props.questionnaireInstanceId)
            .then(response => this.mutateState(s => {
                s.formTemplate = new FormTemplate(response.FormTemplate);
                s.patientName = response.PatientName;
                s.answers = [];
            }));
    }

    public render(): JSX.Element {
        if (!this.state || !this.state.formTemplate)
            return null;

        const templateFormRenderer = new TemplateFormRenderer(this.state.answers, this.state.formTemplate);

        return (
            <Modal
                id={this.componentId}
                dataCy="questionnaireFormPopup-modal"
                title={`${this.state.formTemplate.getComponentName()} - ${this.state.patientName}`}
                onClose={() => this.close()}
                footer={this.renderFooter(templateFormRenderer)}
                top={60}
                width={800}
                showOverlay
                showX
                disableOverlayClose
                roundTopCorner
                draggable
            >
                <div style={{ padding: "0 25px", maxHeight: "650px" }}>
                    <QuestionnaireFormComponent
                        componentName={this.state.formTemplate.getComponentName()}
                        answers={this.state.answers}
                        templateFormRenderer={templateFormRenderer}
                        onAnswerChanged={ans => this.handleOnChanged(ans)}
                    />
                </div>
            </Modal>
        );
    }

    private renderFooter(templateFormRenderer: TemplateFormRenderer): JSX.Element {
        return (
            <div className="p-footer text-right">
                <button className="btn btn-default push-right-5" onClick={() => this.close()}>{cacheStore.GetString("CancelLabel")}</button>
                <button className="btn btn-primary" data-cy="questionnaireFormPopup-submit-button" disabled={!templateFormRenderer.isValid()} onClick={() => this.handleOnSubmit()}>{cacheStore.GetString("SubmitLabel")}</button>
            </div>
        );
    }

    private async handleOnSubmit(): Promise<void> {
        const response = await this.action.submit(
            this.props.questionnaireInstanceId,
            this.state.answers
        );
        this.props.onSubmitted && this.props.onSubmitted(response.Result);
        sessionStore.dispatch(LocalOperations.Sidebar.Sidebar_LoadNotice_Counts);
    }

    private handleOnChanged(answer: QuestionnaireAnswer): void {
        this.mutateState(s => {
            const oldAnswer = s.answers.firstOrDefault(x => x.PropertyName === answer.PropertyName
                && x.PropertyPath === answer.PropertyPath
            );

            if (!oldAnswer) {
                s.answers.push(answer);
            }
            else {
                const index = s.answers.indexOf(oldAnswer);
                s.answers[index] = answer;
            }
        });
    }

    private close(): void {
        this.props.onClose && this.props.onClose();
    }

    private handleError(errors: ErrorMessage[]): void {
        sessionStore.showPopup(errors);
        this.close();
    }
}

export { QuestionnaireFormPopup };
